import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Button,
  Stack,
  alpha,
  useTheme,
  Skeleton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  NotificationsActive as AlertIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  NotificationsOff as MuteIcon,
  CheckCircleOutline as AcknowledgeIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { parseServerTs, toServerDate, formatDateTz, formatUtc } from '../../timezone';

// Severity configuration with enterprise color scheme
const SEVERITY_CONFIG = {
  CRITICAL: {
    color: 'error',
    icon: ErrorIcon,
    bgColor: (theme) => alpha(theme.palette.error.main, 0.08),
    borderColor: (theme) => alpha(theme.palette.error.main, 0.3),
    pulseColor: (theme) => theme.palette.error.main,
  },
  WARNING: {
    color: 'warning',
    icon: WarningIcon,
    bgColor: (theme) => alpha(theme.palette.warning.main, 0.08),
    borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
    pulseColor: (theme) => theme.palette.warning.main,
  },
  INFO: {
    color: 'info',
    icon: InfoIcon,
    bgColor: (theme) => alpha(theme.palette.info.main, 0.08),
    borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
    pulseColor: (theme) => theme.palette.info.main,
  },
};

// Status configuration
const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'error' },
  ACKNOWLEDGED: { label: 'Acknowledged', color: 'warning' },
  MUTED: { label: 'Muted', color: 'default' },
  RESOLVED: { label: 'Resolved', color: 'success' },
};

// Format duration in human-readable format
const formatDuration = (startedAt) => {
  if (!startedAt) return 'Unknown';
  try {
    const date = toServerDate(startedAt);
    if (!date) return 'Unknown';
    return formatDistanceToNow(date, { addSuffix: false });
  } catch {
    return 'Unknown';
  }
};

// Single Alert Row Component
const AlertRow = ({ alert, onAction, isCompact }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const severity = alert.severity || 'INFO';
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.INFO;
  const SeverityIcon = config.icon;
  const statusConfig = STATUS_CONFIG[alert.status] || STATUS_CONFIG.ACTIVE;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    onAction?.(action, alert);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: isCompact ? 1 : 1.5,
        bgcolor: config.bgColor(theme),
        borderLeft: `3px solid ${config.borderColor(theme)}`,
        borderRadius: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: alpha(config.bgColor(theme), 1.5),
          transform: 'translateX(2px)',
        },
      }}
    >
      {/* Severity Icon with pulse animation for critical */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          ...(severity === 'CRITICAL' && alert.status === 'ACTIVE' && {
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: alpha(config.pulseColor(theme), 0.3),
              animation: 'pulse 2s infinite',
            },
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.5)', opacity: 0 },
              '100%': { transform: 'scale(1)', opacity: 0 },
            },
          }),
        }}
      >
        <SeverityIcon
          sx={{
            fontSize: isCompact ? 18 : 20,
            color: `${config.color}.main`,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Alert Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant={isCompact ? 'body2' : 'subtitle2'}
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {alert.name}
        </Typography>
        {!isCompact && alert.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {alert.description}
          </Typography>
        )}
      </Box>

      {/* Status Chip */}
      <Chip
        label={statusConfig.label}
        size="small"
        color={statusConfig.color}
        variant={alert.status === 'ACTIVE' ? 'filled' : 'outlined'}
        sx={{ fontSize: '0.7rem', height: 22 }}
      />

      {/* Duration */}
      <Tooltip title={toServerDate(alert.startedAt) ? `Started: ${formatDateTz(alert.startedAt, { style: 'long' })} (${formatUtc(alert.startedAt)})` : 'Started: Unknown'}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
          }}
        >
          <TimeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {formatDuration(alert.startedAt)}
          </Typography>
        </Box>
      </Tooltip>

      {/* Current Value */}
      {alert.currentValue !== null && alert.currentValue !== undefined && (
        <Tooltip title={`Threshold: ${alert.thresholdValue}`}>
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
            label={typeof alert.currentValue === 'number'
              ? alert.currentValue.toFixed(2)
              : alert.currentValue
            }
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Tooltip>
      )}

      {/* Actions Menu */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {alert.status === 'ACTIVE' && (
          <>
            <MenuItem onClick={() => handleAction('acknowledge')}>
              <ListItemIcon><AcknowledgeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Acknowledge</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleAction('mute')}>
              <ListItemIcon><MuteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Mute</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

// Loading Skeleton
const AlertSkeleton = ({ count = 3 }) => (
  <Stack spacing={1}>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
        <Skeleton variant="circular" width={20} height={20} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
        <Skeleton variant="rounded" width={60} height={22} />
        <Skeleton variant="text" width={50} />
      </Box>
    ))}
  </Stack>
);

// Summary Header Component
const AlertSummaryHeader = ({ alerts, loading }) => {
  const theme = useTheme();

  const counts = {
    critical: alerts?.filter(a => a.severity === 'CRITICAL' && a.status === 'ACTIVE').length || 0,
    warning: alerts?.filter(a => a.severity === 'WARNING' && a.status === 'ACTIVE').length || 0,
    info: alerts?.filter(a => a.severity === 'INFO' && a.status === 'ACTIVE').length || 0,
    acknowledged: alerts?.filter(a => a.status === 'ACKNOWLEDGED').length || 0,
  };

  const totalActive = counts.critical + counts.warning + counts.info;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Skeleton variant="rounded" width={80} height={32} />
        <Skeleton variant="rounded" width={80} height={32} />
        <Skeleton variant="rounded" width={80} height={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
      <Chip
        icon={<AlertIcon sx={{ fontSize: 16 }} />}
        label={`${totalActive} Active`}
        color={totalActive > 0 ? 'error' : 'default'}
        variant={totalActive > 0 ? 'filled' : 'outlined'}
        size="small"
      />
      {counts.critical > 0 && (
        <Chip
          icon={<ErrorIcon sx={{ fontSize: 14 }} />}
          label={`${counts.critical} Critical`}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            borderColor: alpha(theme.palette.error.main, 0.3),
          }}
          variant="outlined"
        />
      )}
      {counts.warning > 0 && (
        <Chip
          icon={<WarningIcon sx={{ fontSize: 14 }} />}
          label={`${counts.warning} Warning`}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.dark,
            borderColor: alpha(theme.palette.warning.main, 0.3),
          }}
          variant="outlined"
        />
      )}
      {counts.acknowledged > 0 && (
        <Chip
          icon={<AcknowledgeIcon sx={{ fontSize: 14 }} />}
          label={`${counts.acknowledged} Acked`}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

/**
 * ActiveAlertsWidget - Enterprise Dashboard Widget
 * Displays live view of triggered alerts filtered by selected alert rule definitions
 */
const ActiveAlertsWidget = ({
  alerts = [],
  loading = false,
  error = null,
  onRefresh,
  onAlertAction,
  maxAlerts = 10,
  showSummary = true,
  compact = false,
  sx = {},
}) => {
  const theme = useTheme();

  // Sort alerts: Active first, then by severity (Critical > Warning > Info), then by time
  const sortedAlerts = [...alerts].sort((a, b) => {
    // Status priority: ACTIVE first
    const statusOrder = { ACTIVE: 0, ACKNOWLEDGED: 1, MUTED: 2, RESOLVED: 3 };
    const statusDiff = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
    if (statusDiff !== 0) return statusDiff;

    // Severity priority: CRITICAL > WARNING > INFO
    const severityOrder = { CRITICAL: 0, WARNING: 1, INFO: 2 };
    const severityDiff = (severityOrder[a.severity] || 2) - (severityOrder[b.severity] || 2);
    if (severityDiff !== 0) return severityDiff;

    // Most recent first
    return parseServerTs(b.startedAt) - parseServerTs(a.startedAt);
  });

  const displayedAlerts = sortedAlerts.slice(0, maxAlerts);
  const hasMore = sortedAlerts.length > maxAlerts;
  const activeCount = alerts.filter(a => a.status === 'ACTIVE').length;

  // Handle alert action (acknowledge, mute, view)
  const handleAlertAction = useCallback((action, alert) => {
    onAlertAction?.(action, alert);
  }, [onAlertAction]);

  if (error) {
    return (
      <Paper sx={{ p: 2, height: '100%', ...sx }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'error.main',
          }}
        >
          <ErrorIcon sx={{ fontSize: 40, mb: 1, opacity: 0.6 }} />
          <Typography variant="body2" color="error">
            {error}
          </Typography>
          {onRefresh && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{ mt: 1 }}
            >
              Retry
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
      {/* Summary Header */}
      {showSummary && <AlertSummaryHeader alerts={alerts} loading={loading} />}

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Alerts List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && alerts.length === 0 ? (
          <AlertSkeleton count={compact ? 3 : 5} />
        ) : displayedAlerts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3, color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary">
              No active alerts
            </Typography>
            <Typography variant="caption" color="text.disabled">
              All systems operating normally
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {displayedAlerts.map((alert) => (
              <AlertRow
                key={alert.id || alert.guid}
                alert={alert}
                onAction={handleAlertAction}
                isCompact={compact}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Show More indicator */}
      {hasMore && (
        <Box sx={{ pt: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            +{sortedAlerts.length - maxAlerts} more alerts
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ActiveAlertsWidget;
